import { useEffect } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { Download } from 'lucide-react';
import { StyledBoldDescription } from '../atoms/typography/body/BodyTypography';
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import { FormattedMessage } from 'react-intl';
import { pxToRem } from '../../utils/getFontValue';
import { ReportCard } from './ReportCard';
import { QueryTable } from './QueryTable';
import {
  setPerformanceFilter,
  initReports,
  downloadPerformanceReport,
  fetchCategoryCounts,
} from '../../redux/slice/viewReportSlice';

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(14)};
  margin: ${pxToRem(140)};
`;

function ViewReport() {
  const dispatch = useAppDispatch();

  const projectId = useAppSelector(
    (state: RootState) => state.userProject.currentProject.project_id
  );
  const currentRunId = useAppSelector(
    (state: RootState) => state.userProject.currentRunId
  );

  const viewReportState = useAppSelector(
    (state: RootState) => state.viewReports
  );

  const { reports, reportIds, error, isDownloading, downloadError } =
    viewReportState;

  useEffect(() => {
    if (reportIds.length === 0) {
      dispatch(initReports());
    }
  }, [dispatch, reportIds.length]);

  useEffect(() => {
    if (projectId && currentRunId != null && reportIds.length > 0) {
      reportIds.forEach((reportId) => {
        dispatch(fetchCategoryCounts(projectId, currentRunId, reportId));
      });
    }
  }, [dispatch, projectId, currentRunId, reportIds]);

  const handlePerformanceFilterChange = (
    reportId: string,
    filter: 'all' | 'green' | 'yellow' | 'red'
  ) => {
    dispatch(setPerformanceFilter({ reportId, filter }));

    const filterHandler = (window as any)[`filterHandler_${reportId}`];
    if (filterHandler) {
      filterHandler(filter);
    }
  };

  const handleDownloadPerformanceReport = () => {
    if (!projectId || currentRunId == null) return;
    dispatch(downloadPerformanceReport(projectId, currentRunId));
  };

  if (error) {
    return (
      <StyledBox>
        <StyledBoldDescription style={{ color: 'red' }}>
          {error}
        </StyledBoldDescription>
      </StyledBox>
    );
  }

  return (
    <div
      className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans"
      style={{ fontFamily: "'Lato', sans-serif" }}
    >
      <div>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-gray-600 text-sm">
            <FormattedMessage id={'steps.5.description3'} />
          </p>
          {reportIds.length > 0 && (
            <button
              onClick={handleDownloadPerformanceReport}
              disabled={isDownloading}
              className="flex items-center justify-center gap-2 bg-[#1e1b4b] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {isDownloading ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download Performance Report
                </>
              )}
            </button>
          )}
        </div>

        {downloadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {downloadError}
          </div>
        )}

        <div className="space-y-4">
          {reportIds.map((reportId, index) => {
            const report = reports[reportId];
            if (!report) return null;

            return (
              <ReportCard
                key={reportId}
                id={reportId}
                title={report.title}
                description={report.description}
                date={report.date}
                defaultExpanded={index === 0}
                isSelected={false}
                onSelectChange={() => {}}
                selectable={false}
                querySummary={report.querySummary}
                performanceFilter={report.performanceFilter}
                onPerformanceFilterChange={(filter) =>
                  handlePerformanceFilterChange(reportId, filter)
                }
              >
                <QueryTable reportId={reportId} projectId={projectId} />
              </ReportCard>
            );
          })}
        </div>

        {reportIds.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No reports available yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewReport;
